'use client';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DropdownMenuCheckboxItemProps } from '@radix-ui/react-dropdown-menu';
import { ColumnFiltersState, PaginationState } from '@tanstack/react-table';
import { Filter, FilterX } from 'lucide-react';
import { Dispatch, SetStateAction, useCallback, useMemo } from 'react';

type Checked = DropdownMenuCheckboxItemProps['checked'];

interface FilterOption {
  id: string;
  label: string;
}

export interface FilterConfig {
  id: string;
  label: string;
  options: FilterOption[];
}

interface Props {
  columnFilters: ColumnFiltersState;
  setColumnFilters: Dispatch<SetStateAction<ColumnFiltersState>>;
  filters: FilterConfig[];
  onPaginationChange: Dispatch<SetStateAction<PaginationState>>;
}

export const DataTableFilter = ({ columnFilters, setColumnFilters, filters, onPaginationChange }: Props) => {
  const filtersEnabled = useMemo(() => columnFilters.some((filter) => filter.id !== 'search'), [columnFilters]);

  const isOptionChecked = useCallback(
    (filterId: string, optionId: string) => columnFilters.some((f) => f.id === filterId && f.value === optionId),
    [columnFilters]
  );

  const toggleOption = useCallback(
    (filterId: string, optionId: string) => (checked: Checked) => {
      setColumnFilters((prev) =>
        checked ? [...prev, { id: filterId, value: optionId }] : prev.filter((f) => !(f.id === filterId && f.value === optionId))
      );
      onPaginationChange((prev: PaginationState) => ({ ...prev, pageIndex: 0 }));
    },
    [setColumnFilters, onPaginationChange]
  );

  const clearAll = useCallback(() => {
    setColumnFilters((prev) => prev.filter((f) => f.id === 'search'));
    onPaginationChange((prev: PaginationState) => ({ ...prev, pageIndex: 0 }));
  }, [setColumnFilters, onPaginationChange]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant={filtersEnabled ? 'default' : 'secondary'}>
          <Filter />
          Filters
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuGroup>
          <DropdownMenuItem disabled={!filtersEnabled} onClick={clearAll}>
            <FilterX />
            Clear All
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          {filters.map(({ id: filterId, label, options }) => (
            <DropdownMenuSub key={filterId}>
              <DropdownMenuSubTrigger>{label}</DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent>
                  {options.map(({ id: optionId, label: optionLabel }) => (
                    <DropdownMenuCheckboxItem
                      key={optionId}
                      checked={isOptionChecked(filterId, optionId)}
                      onCheckedChange={toggleOption(filterId, optionId)}
                    >
                      {optionLabel}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub>
          ))}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
